import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import Loading from "../Components/Loading";
import { API_URL } from "../config/api";

const StoreAssetsView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      toast.error("Asset identifier is missing.");
      navigate("/store-assets");
      return;
    }

    fetchAsset(id);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAsset = async (assetId) => {
    if (!API_URL) {
      toast.error(
        "API URL is not configured. Please set REACT_APP_API_URL in environment variables."
      );

      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        action: "get-by-id-asset",
        id: assetId,
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = response.data;

      if (result?.success) {
        setAsset(result.data);
      } else {
        toast.error(result?.message || "Failed to fetch asset");
      }
    } catch (error) {
      console.error("Error fetching asset:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again.";

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);

      if (withTime) {
        return date.toLocaleString("en-GB", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const renderDetailItem = (label, value) => (
    <div className="employee-detail-item" key={label}>
      <span className="employee-detail-label">{label}</span>

      <span className="employee-detail-value">
        {value ?? "-"}
      </span>
    </div>
  );

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">

          {/* Header */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Asset Profile
                <i className="fas fa-laptop ml-2 employee-title-icon"></i>
              </h1>

              <p className="employee-description">
                Comprehensive view of the asset information and audit details.
              </p>
            </div>

            <div className="col-md-4 employee-view-header-actions">
              <button
                type="button"
                className="employee-back-btn"
                onClick={() => navigate("/store-assets")}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Back to Manage Assets
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="card employee-content-card">
            <div className="card-body employee-view-body">

              {loading ? (
                <Loading size={180} message="Loading asset details..." />
              ) : !asset ? (
                <div className="employee-empty">
                  Asset details are not available.
                </div>
              ) : (
                <>

                  {/* Asset Information */}
                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">
                        Asset Information
                      </h3>
                    </div>

                    <div className="employee-detail-grid three-column">

                      {renderDetailItem(
                        "Asset Name",
                        asset.assetName
                      )}

                      {renderDetailItem(
                        "Brand",
                        asset.brand
                      )}

                      {renderDetailItem(
                        "Status",
                       asset.status 
                      )}

                      {renderDetailItem(
                        "Purchase Date",
                        formatDate(asset.purchaseDate)
                      )}

                      {renderDetailItem(
                        "Warranty Expiry",
                        formatDate(asset.warrantyExpiry)
                      )}

                    </div>
                  </section>

                  {/* Description */}
               <section className="employee-section">
  <div className="employee-section-header">
    <h3 className="employee-section-title">
      Description
    </h3>
  </div>

  <div className="employee-detail-grid one-column">
    {renderDetailItem(
      "Asset Description",
      asset.description || "-"
    )}
  </div>
</section>

                  {/* Audit Trail */}
                  <section className="employee-section">
                    <div className="employee-section-header">
                      <h3 className="employee-section-title">
                        Audit Trail
                      </h3>
                    </div>

                    <div className="employee-detail-grid two-column">

                      {renderDetailItem(
                        "Created On",
                        formatDate(asset.createdAt, true)
                      )}

                      {renderDetailItem(
                        "Last Updated",
                        formatDate(asset.updatedAt, true)
                      )}

                    </div>
                  </section>

                  {/* Actions */}
                  <div className="form-actions d-flex justify-content-center align-items-center">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() =>
                        navigate(`/store-assets-edit/${asset.id}`, {
                          state: {
                            asset,
                          },
                        })
                      }
                    >
                      Edit Asset
                    </button>
                  </div>

                </>
              )}

            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StoreAssetsView;